import {
  useCallback,
  MouseEvent,
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
} from 'react';
import { generatePath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  PUBLIC_PRICING,
  PUBLIC_BLOG,
  PUBLIC_CONTACT_US,
  PUBLIC_SCOPE_AND_SEQUENCE,
  PUBLIC_TERMS,
  PUBLIC_TEACHER_BENEFITS,
  PUBLIC_SCHOOL_BENEFITS,
  PUBLIC_SUBJECT,
  PUBLIC_STIMULUS,
  PUBLIC_PRICING_FAQS,
  PUBLIC_ABOUT,
} from 'global/public-routes.constants';
import FooterSocials from 'approot/shared/layout/footer/social/footer-socials';
import FooterLogo from 'approot/shared/layout/footer/footer-logo';
import * as Styled from 'approot/shared/layout/footer/mini-footer.style';
import Accordion from 'approot/shared/accordion/accordion';
import AccordionToggleNew from 'approot/shared/accordion/accordion-toggle-new';
import {
  pushFooterNavOpenDataEvent,
  pushFooterNavSublinkDataEvent,
} from 'approot/shared/layout/navigation/navigation.utils';
import FooterRegions from 'approot/shared/layout/footer/regions/footer-regions';
import { useLayoutLinks } from 'approot/shared/layout/layout.hooks';
import {
  NAV_ABOUT_LABEL,
  NAV_CONTACT_US_LABEL,
  NAV_DISCIPLINES_LABEL,
  NAV_FAQS_LABEL,
  NAV_FOR_SCHOOLS_LABEL,
  NAV_FOR_TEACHERS_LABEL,
  NAV_LESSONS,
  NAV_LESSONS_LABEL,
  NAV_PRICING_LABEL,
  NAV_PRODUCT,
  NAV_PRODUCT_LABEL,
  NAV_SCOPE_AND_SEQUENCE_LABEL,
  NAV_SEND_MESSAGE_LABEL,
  NAV_STIMULUS_LIBRARY_LABEL,
  NAV_SUPPORT,
  NAV_SUPPORT_LABEL,
  NAV_TEACHING_BLOG_LABEL,
  NAV_TERMS_AND_PRIVACY_LABEL,
} from 'approot/shared/layout/navigation/mega/mega-nav.constants';
import { spacing16 } from 'approot/shared/styles/spacing.style';
import { setContentFilterData } from 'approot/shared/filters/filters.apollo';
import { useGetStartupData } from 'approot/shared/graphql/startup-data/startup-data.hooks';
import { INTERCOM_LAUNCH_TARGET_CLASS } from 'lib/intercom/intercom';
import { NAMESPACE_SUPPORT } from 'internationalise';
import { PRIVATE_ACCOUNT_REFER } from 'global/private-routes.constants';
import useUserData from 'approot/shared/user/user-data';
import { FlexSpaceBetween } from 'approot/shared/styles/flex.style';

export const MiniFooterLink = ({
  slug,
  menu,
  label,
  onClick,
}: {
  slug: string;
  menu: string;
  label: string;
  onClick?: (e: MouseEvent) => void;
}) => (
  <Styled.MiniFooterLink
    to={slug}
    onClick={e => {
      onClick?.(e);
      pushFooterNavSublinkDataEvent(menu, label);
    }}
  >
    {label}
  </Styled.MiniFooterLink>
);

export const MiniFooterAnchor = ({
  href,
  menu,
  label,
  onClick,
  ...rest
}: {
  href: string;
  menu: string;
  label: string;
  onClick?: (e: MouseEvent) => void;
} & AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <Styled.MiniFooterAnchor
    href={href}
    onClick={e => {
      onClick?.(e);
      pushFooterNavSublinkDataEvent(menu, label);
    }}
    {...rest}
  >
    {label}
  </Styled.MiniFooterAnchor>
);

export const MiniFooterButton = ({
  menu,
  label,
  onClick,
  ...rest
}: {
  menu: string;
  label: string;
  onClick?: (e: MouseEvent) => void;
} & ButtonHTMLAttributes<HTMLButtonElement>) => (
  <Styled.MiniFooterButton
    onClick={e => {
      onClick?.(e);
      pushFooterNavSublinkDataEvent(menu, label);
    }}
    {...rest}
  >
    {label}
  </Styled.MiniFooterButton>
);

const MiniFooter = () => {
  const { data, disciplines } = useGetStartupData();
  const { t } = useTranslation(NAMESPACE_SUPPORT);
  const subjects = data?.startupData.subjects;
  const { goToScopeAndSequence, goToFaqs } = useLayoutLinks();
  const { isSignedIn } = useUserData();

  const handleNavClick = useCallback((isExpanded: boolean, label: string) => {
    if (isExpanded) {
      pushFooterNavOpenDataEvent(label);
    }
  }, []);

  return (
    <Styled.MiniFooter data-selector="footer-section">
      <Styled.MiniFooterWrapper>
        <FooterLogo />
        <Styled.MiniFooterLinks>
          <Accordion id="nav">
            <AccordionToggleNew
              contentPaddingBottom={spacing16}
              withDividers
              onClick={(e, isExpanded) =>
                handleNavClick(
                  isExpanded,
                  disciplines ? NAV_DISCIPLINES_LABEL : NAV_LESSONS_LABEL
                )
              }
              labelComponent={
                <Styled.MiniFooterLabel>
                  {disciplines ? NAV_DISCIPLINES_LABEL : NAV_LESSONS_LABEL}
                </Styled.MiniFooterLabel>
              }
            >
              {disciplines
                ? disciplines.map(discipline => (
                    <MiniFooterLink
                      key={discipline.name}
                      slug={discipline.slug}
                      menu={NAV_LESSONS}
                      label={discipline.label}
                      onClick={() =>
                        setContentFilterData({
                          triggerContentRefresh: true,
                        })
                      }
                    />
                  ))
                : subjects?.map(subject => (
                    <MiniFooterLink
                      key={subject.name}
                      slug={generatePath(PUBLIC_SUBJECT, {
                        subject: subject.name,
                      })}
                      menu={NAV_LESSONS}
                      label={subject.label}
                      onClick={() =>
                        setContentFilterData({
                          triggerContentRefresh: true,
                        })
                      }
                    />
                  ))}
              <MiniFooterLink
                slug={PUBLIC_STIMULUS}
                menu={NAV_LESSONS}
                label={NAV_STIMULUS_LIBRARY_LABEL}
              />
              <MiniFooterLink
                slug={PUBLIC_SCOPE_AND_SEQUENCE}
                menu={NAV_LESSONS}
                label={NAV_SCOPE_AND_SEQUENCE_LABEL}
                onClick={e => {
                  goToScopeAndSequence(e);
                }}
              />
            </AccordionToggleNew>
            <AccordionToggleNew
              contentPaddingBottom={spacing16}
              withDividers
              onClick={(e, isExpanded) =>
                handleNavClick(isExpanded, NAV_PRODUCT_LABEL)
              }
              labelComponent={
                <Styled.MiniFooterLabel>
                  {NAV_PRODUCT_LABEL}
                </Styled.MiniFooterLabel>
              }
            >
              <MiniFooterLink
                slug={PUBLIC_TEACHER_BENEFITS}
                menu={NAV_PRODUCT}
                label={NAV_FOR_TEACHERS_LABEL}
              />
              <MiniFooterLink
                slug={PUBLIC_SCHOOL_BENEFITS}
                menu={NAV_PRODUCT}
                label={NAV_FOR_SCHOOLS_LABEL}
              />
              <MiniFooterLink
                slug={PUBLIC_PRICING}
                menu={NAV_PRODUCT}
                label={NAV_PRICING_LABEL}
              />
              <MiniFooterLink
                slug={PUBLIC_ABOUT}
                menu={NAV_PRODUCT}
                label={NAV_ABOUT_LABEL}
              />
              <MiniFooterLink
                slug={PUBLIC_TERMS}
                menu={NAV_PRODUCT}
                label={NAV_TERMS_AND_PRIVACY_LABEL}
              />
            </AccordionToggleNew>
            <AccordionToggleNew
              contentPaddingBottom={spacing16}
              withDividers
              onClick={(e, isExpanded) =>
                handleNavClick(isExpanded, NAV_SUPPORT_LABEL)
              }
              labelComponent={
                <Styled.MiniFooterLabel>
                  {NAV_SUPPORT_LABEL}
                </Styled.MiniFooterLabel>
              }
            >
              <MiniFooterAnchor
                target="_blank"
                href={t('helpCentreLink')}
                menu={NAV_SUPPORT}
                label={t('helpCentreLinkLabel')}
              />
              <MiniFooterButton
                type="button"
                className={INTERCOM_LAUNCH_TARGET_CLASS}
                aria-haspopup="dialog"
                menu={NAV_SUPPORT}
                label={NAV_SEND_MESSAGE_LABEL}
              />
              <MiniFooterLink
                slug={PUBLIC_PRICING_FAQS}
                menu={NAV_SUPPORT}
                label={NAV_FAQS_LABEL}
                onClick={() => goToFaqs()}
              />
              <MiniFooterLink
                slug={PUBLIC_BLOG}
                menu={NAV_SUPPORT}
                label={NAV_TEACHING_BLOG_LABEL}
              />
              <MiniFooterLink
                slug={PUBLIC_CONTACT_US}
                menu={NAV_SUPPORT}
                label={NAV_CONTACT_US_LABEL}
              />
            </AccordionToggleNew>
          </Accordion>
        </Styled.MiniFooterLinks>
        <Styled.MiniFooterSocialWrapper>
          <FlexSpaceBetween>
            <FooterSocials />
            <FooterRegions />
          </FlexSpaceBetween>
          {isSignedIn && (
            <Styled.MiniFooterReferLink to={PRIVATE_ACCOUNT_REFER}>
              Love Inquisitive? Refer and earn credits
            </Styled.MiniFooterReferLink>
          )}
        </Styled.MiniFooterSocialWrapper>
        <Styled.MiniFooterTextStyled>
          &copy; Inquisitive {t('company')}
        </Styled.MiniFooterTextStyled>
      </Styled.MiniFooterWrapper>
    </Styled.MiniFooter>
  );
};

export default MiniFooter;
